import React, { useEffect, useState } from "react";
import {
  Card, CardContent, Typography, Button, Grid, Alert, Box, Chip, Stack,
} from "@mui/material";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import { getPollVote, submitPollVote } from "../firebaseService";

export default function PollVote({ poll, memberId }) {
  const [selected, setSelected] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);

  const deadline = poll.deadline ? new Date(poll.deadline + "T23:59:59") : null;
  const isAfterDeadline = deadline ? new Date() > deadline : false;

  useEffect(() => {
    if (!memberId || !poll.id) return;
    const fetchVote = async () => {
      const existingVote = await getPollVote(poll.id, memberId);
      if (existingVote) {
        setSelected(existingVote);
        setHasVoted(true);
      }
    };
    fetchVote();
  }, [memberId, poll.id]);

  const handleVote = async (option) => {
    if (!memberId || isAfterDeadline || !poll.published) return;
    await submitPollVote(poll.id, memberId, option);
    setSelected(option);
    setHasVoted(true);
  };

  if (!memberId) return null;

  return (
    <Card
      sx={{
        maxWidth: 700,
        margin: "1.5rem auto",
        borderRadius: 4,
        overflow: "hidden",
        border: `2px solid ${poll.color || "#5b8def"}33`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: poll.color || "#5b8def",
          color: "white",
          py: 2,
          px: 3,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {poll.emoji ? (
          <Typography fontSize="1.5rem">{poll.emoji}</Typography>
        ) : (
          <HowToVoteIcon />
        )}
        <Typography variant="h5" fontWeight="bold">
          {poll.title}
        </Typography>
        {deadline && (
          <Chip
            label={`bis ${deadline.toLocaleDateString("de-DE")}`}
            size="small"
            sx={{ ml: "auto", background: "rgba(255,255,255,0.2)", color: "white" }}
          />
        )}
      </Box>

      {/* Content */}
      <CardContent sx={{ px: 4, pt: 3, pb: 4 }}>
        <Stack spacing={3}>
          {isAfterDeadline ? (
            <Alert severity="info">
              Die Abstimmung ist abgeschlossen. Danke fuer deine Stimme!
            </Alert>
          ) : poll.description ? (
            <Typography color="text.secondary">{poll.description}</Typography>
          ) : null}

          {/* Options */}
          <Grid container spacing={2} justifyContent="center">
            {poll.options.map((option) => (
              <Grid item xs={6} sm={4} key={option}>
                <Button
                  fullWidth
                  variant={selected === option ? "contained" : "outlined"}
                  color={selected === option ? "success" : "primary"}
                  onClick={() => handleVote(option)}
                  disabled={isAfterDeadline || !poll.published}
                  sx={{
                    height: "3.2rem",
                    fontSize: "0.95rem",
                    textTransform: "none",
                    fontWeight: "bold",
                    backgroundColor: selected === option ? "#66bb6a" : undefined,
                    "&:hover": {
                      backgroundColor: selected === option ? "#4caf50" : `${poll.color || "#5b8def"}22`,
                    },
                  }}
                >
                  {option}
                </Button>
              </Grid>
            ))}
          </Grid>

          {hasVoted && selected && (
            <Alert severity="success" sx={{ fontSize: "1rem" }}>
              Du hast fuer <strong>{selected}</strong> abgestimmt!
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
